import { createTheme } from '@mui/material/styles';

// Industry Design System
// Primary: #5980a6 | Dark: #416180 | Light: #94bce3 | Background: #f2f2f3 | Text: #1d1f20
// Font: Barlow / Barlow Condensed

const headingFamily = '"Barlow Condensed", system-ui, sans-serif';
const dividerColor = 'rgba(29,31,32,0.16)';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#5980a6',
      light: '#94bce3',
      dark: '#416180',
      contrastText: '#f2f2f3',
    },
    secondary: {
      main: '#416180',
      light: '#94bce3',
      dark: '#1d2d3d',
      contrastText: '#f2f2f3',
    },
    background: {
      default: '#f2f2f3',
      paper: '#f2f2f3',
    },
    text: {
      primary: '#1d1f20',
      secondary: 'rgba(29,31,32,0.55)',
    },
    divider: dividerColor,
    info: {
      main: '#5980a6',
      light: '#94bce3',
      dark: '#416180',
      contrastText: '#f2f2f3',
    },
    success: {
      main: '#10B981',
      light: '#34D399',
      dark: '#059669',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#F59E0B',
      light: '#FCD34D',
      dark: '#D97706',
      contrastText: '#ffffff',
    },
    error: {
      main: '#EF4444',
      light: '#FCA5A5',
      dark: '#DC2626',
      contrastText: '#ffffff',
    },
    grey: {
      50: '#f5f5f8',
      100: '#e7e7ea',
      200: '#d4d4d7',
      300: '#b7b7ba',
      400: '#98989b',
      500: '#7a7a7d',
      600: '#5d5d60',
      700: '#424244',
      800: '#2b2b2d',
      900: '#1d1f20',
    },
  },
  typography: {
    fontFamily: '"Barlow", system-ui, sans-serif',
    fontSize: 15,
    h1: {
      fontFamily: headingFamily,
      fontWeight: 600,
      fontSize: '2.75rem',
      lineHeight: 1.12,
      letterSpacing: '-0.015em',
      color: '#1d1f20',
    },
    h2: {
      fontFamily: headingFamily,
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.12,
      letterSpacing: '-0.015em',
      color: '#1d1f20',
    },
    h3: {
      fontFamily: headingFamily,
      fontWeight: 600,
      fontSize: '1.5625rem',
      lineHeight: 1.12,
      letterSpacing: '-0.015em',
      color: '#1d1f20',
    },
    h4: {
      fontFamily: headingFamily,
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.12,
      letterSpacing: '-0.015em',
      color: '#1d1f20',
    },
    h5: {
      fontFamily: headingFamily,
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.12,
      letterSpacing: '-0.015em',
      color: '#1d1f20',
    },
    h6: {
      fontFamily: headingFamily,
      fontWeight: 600,
      fontSize: '0.875rem',
      lineHeight: 1.12,
      letterSpacing: '-0.015em',
      color: '#1d1f20',
    },
    body1: { color: '#1d1f20', lineHeight: 1.55, fontSize: '0.9375rem' },
    body2: { color: 'rgba(29,31,32,0.55)', lineHeight: 1.55, fontSize: '0.875rem' },
    subtitle1: { fontWeight: 500, color: '#1d1f20' },
    subtitle2: { fontWeight: 600, color: '#1d1f20' },
    caption: { color: 'rgba(29,31,32,0.55)', fontSize: '0.75rem' },
    button: {
      fontFamily: headingFamily,
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '0.875rem',
      letterSpacing: '0.01em',
    },
    overline: {
      fontWeight: 700,
      letterSpacing: '0.1em',
      fontSize: '0.6875rem',
      textTransform: 'uppercase',
      color: 'rgba(29,31,32,0.55)',
    },
  },
  shape: {
    borderRadius: 0,
  },
  shadows: [
    'none',
    '0 1px 2px rgba(43,43,45,.14)',
    '0 1px 2px rgba(43,43,45,.14)',
    '0 4px 12px rgba(43,43,45,.14)',
    '0 12px 32px rgba(43,43,45,.22)',
    '0 12px 32px rgba(43,43,45,.22)',
    '0 12px 32px rgba(43,43,45,.22)',
    ...Array(18).fill('none'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#f2f2f3',
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: `1px solid ${dividerColor}`,
          borderRadius: 0,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 0,
          fontFamily: headingFamily,
          fontWeight: 600,
          fontSize: '0.875rem',
          padding: '8px 20px',
          letterSpacing: '0.01em',
          boxShadow: 'none',
          '&:focus-visible': {
            outline: '2px solid #5980a6',
            outlineOffset: '2px',
          },
          '&.Mui-disabled': {
            opacity: 0.45,
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
            backgroundColor: '#597ea3',
          },
        },
        outlined: {
          borderWidth: '1px',
          borderColor: dividerColor,
          color: '#1d1f20',
          '&:hover': {
            borderWidth: '1px',
            borderColor: '#5980a6',
            backgroundColor: 'rgba(89,128,166,0.07)',
          },
        },
        text: {
          '&:hover': {
            backgroundColor: 'rgba(29,31,32,0.07)',
          },
        },
        sizeSmall: { padding: '5px 14px', fontSize: '0.8125rem' },
        sizeLarge: { padding: '12px 28px', fontSize: '1rem' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: '0 1px 2px rgba(43,43,45,.14)',
          border: `1px solid ${dividerColor}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': { paddingBottom: 20 },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#f2f2f3',
          color: '#1d1f20',
          boxShadow: 'none',
          borderBottom: `1px solid ${dividerColor}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#f2f2f3',
          borderRight: '1px solid rgba(29,31,32,0.16)',
          boxShadow: 'none',
          border: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          transition: 'all 0.15s ease',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: '0.6875rem',
          borderRadius: 0,
          height: 22,
        },
        colorPrimary: {
          backgroundColor: '#eef6ff',
          color: '#2c455d',
          fontWeight: 600,
        },
        colorSuccess: {
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          color: '#059669',
          fontWeight: 600,
        },
        colorWarning: {
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          color: '#D97706',
          fontWeight: 600,
        },
        colorError: {
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          color: '#DC2626',
          fontWeight: 600,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          maxHeight: 'calc(100vh - 300px)',
          overflow: 'auto',
        },
      },
    },
    MuiTable: {
      defaultProps: {
        stickyHeader: true,
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#f2f2f3 !important',
            fontWeight: 600,
            color: 'rgba(29,31,32,0.6)',
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            borderBottom: `1px solid ${dividerColor}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(29,31,32,0.07)',
          },
          '&:last-child .MuiTableCell-body': {
            borderBottom: 0,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: dividerColor,
          fontSize: '0.875rem',
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'medium' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 0,
            backgroundColor: '#e9e9ea',
            '& fieldset': { borderColor: dividerColor, borderWidth: '1px' },
            '&:hover': {
              backgroundColor: '#e2e2e3',
            },
            '&:hover fieldset': { borderColor: dividerColor },
            '&.Mui-focused': {
              backgroundColor: '#f2f2f3',
            },
            '&.Mui-focused fieldset': { borderColor: '#5980a6', borderWidth: '1px' },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(29,31,32,0.55)',
            fontWeight: 500,
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#5980a6',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: '#e9e9ea',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: dividerColor, borderWidth: '1px' },
          '&:hover': { backgroundColor: '#e2e2e3' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: dividerColor },
          '&.Mui-focused': { backgroundColor: '#f2f2f3' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#5980a6', borderWidth: '1px' },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        outlined: {
          borderRadius: 0,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          boxShadow: '0 12px 32px rgba(43,43,45,.22)',
          border: `1px solid ${dividerColor}`,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: headingFamily,
          fontWeight: 600,
          fontSize: '1.125rem',
          color: '#1d1f20',
          paddingBottom: 8,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          fontWeight: 500,
          fontSize: '0.875rem',
        },
        standardError: {
          backgroundColor: 'rgba(239, 68, 68, 0.08)',
          color: '#DC2626',
          border: '1px solid rgba(239, 68, 68, 0.2)',
        },
        standardSuccess: {
          backgroundColor: 'rgba(16, 185, 129, 0.08)',
          color: '#059669',
          border: '1px solid rgba(16, 185, 129, 0.2)',
        },
        standardWarning: {
          backgroundColor: 'rgba(245, 158, 11, 0.08)',
          color: '#D97706',
          border: '1px solid rgba(245, 158, 11, 0.2)',
        },
        standardInfo: {
          backgroundColor: 'rgba(89, 128, 166, 0.08)',
          color: '#416180',
          border: '1px solid rgba(89, 128, 166, 0.2)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1d1f20',
          borderRadius: 0,
          fontSize: '0.75rem',
          fontWeight: 500,
          padding: '5px 10px',
        },
        arrow: {
          color: '#1d1f20',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: dividerColor,
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 700,
          fontSize: '0.65rem',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: '0.875rem',
          borderRadius: 0,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          transition: 'all 0.15s ease',
          '&:hover': {
            backgroundColor: 'rgba(29,31,32,0.07)',
          },
          '&:focus-visible': {
            outline: '2px solid #5980a6',
            outlineOffset: '2px',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          border: `1px solid ${dividerColor}`,
          borderRadius: 0,
          minHeight: 36,
          padding: 0,
        },
        indicator: {
          display: 'none',
        },
        flexContainer: {
          gap: 0,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: headingFamily,
          fontWeight: 600,
          fontSize: '0.875rem',
          minHeight: 36,
          padding: '6px 16px',
          color: '#1d1f20',
          borderRight: `1px solid ${dividerColor}`,
          '&:last-of-type': {
            borderRight: 'none',
          },
          '&.Mui-selected': {
            backgroundColor: '#5980a6',
            color: '#f2f2f3',
            fontWeight: 600,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          boxShadow: '0 12px 32px rgba(43,43,45,.22)',
          border: `1px solid ${dividerColor}`,
          backgroundImage: 'none',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          boxShadow: '0 12px 32px rgba(43,43,45,.22)',
          border: `1px solid ${dividerColor}`,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          fontSize: '0.875rem',
          '&:hover': { backgroundColor: 'rgba(29,31,32,0.07)' },
          '&.Mui-selected': { backgroundColor: 'rgba(89,128,166,0.1)', fontWeight: 600 },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: '#d4d4d7',
          height: 6,
        },
        bar: {
          borderRadius: 0,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { width: 46, height: 26, padding: 0 },
        switchBase: {
          padding: 1,
          '&.Mui-checked': { transform: 'translateX(20px)' },
        },
        thumb: { width: 24, height: 24, boxShadow: 'none' },
        track: { borderRadius: 13, opacity: 1, backgroundColor: '#d4d4d7' },
      },
    },
  },
});

export default theme;
